import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, DollarSign, ExternalLink, ReceiptText, RefreshCw, Repeat2, UserPlus, Users } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { VerifiedName } from "../../components/VerifiedBadge";

const RANGE_OPTIONS = [
  { key: "24h", label: "24h" },
  { key: "today", label: "Today" },
  { key: "7d", label: "7d" },
  { key: "month", label: "Month" },
  { key: "90d", label: "90d" },
];

export default function AdminDepositors() {
  const [rangeFilter, setRangeFilter] = useState("24h");
  const [tab, setTab] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState({ stats: {}, rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ range_filter: rangeFilter, tab });
      if (startDate) params.set("start", startDate);
      if (endDate) params.set("end", endDate);
      const response = await api.get(`/admin/depositors?${params.toString()}`);
      setData(response.data || { stats: {}, rows: [] });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [endDate, rangeFilter, startDate, tab]);

  useEffect(() => {
    load();
  }, [load]);
  useAdminAutoRefresh(() => load({ quiet: true }));

  const stats = data.stats || {};
  const rows = data.rows || [];
  const rangeLabel = useMemo(() => {
    if (startDate || endDate) return "custom range";
    return RANGE_OPTIONS.find((item) => item.key === rangeFilter)?.label || "24h";
  }, [endDate, rangeFilter, startDate]);

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Depositors</h1>
          <p className="mt-1 text-sm text-slate-400">All depositors and first-time depositors - {rangeLabel}.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                setRangeFilter(option.key);
                setStartDate("");
                setEndDate("");
              }}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                rangeFilter === option.key && !startDate && !endDate
                  ? "border-amber-400 bg-amber-400 text-black"
                  : "border-white/10 bg-[#101012] text-slate-200 hover:border-amber-400/50"
              }`}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            className="rounded-full border border-white/10 bg-[#101012] p-2 text-slate-200 hover:border-amber-400/50"
            disabled={loading}
            aria-label="Refresh depositors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-amber-500/25 bg-[#121214] p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <FilterPill active={tab === "all"} onClick={() => setTab("all")}>
              All Depositors ({stats.all_depositors || 0})
            </FilterPill>
            <FilterPill active={tab === "new"} onClick={() => setTab("new")}>
              New Depositors ({stats.new_depositors || 0})
            </FilterPill>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <DateInput value={startDate} onChange={setStartDate} />
            <DateInput value={endDate} onChange={setEndDate} />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-500/30 bg-red-950/40 px-4 py-3 text-sm font-semibold text-red-200">
          {error}
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <StatCard icon={Users} label="All depositors" value={stats.all_depositors || 0} tone="gold" />
        <StatCard icon={UserPlus} label="New depositors" value={stats.new_depositors || 0} />
        <StatCard icon={Repeat2} label="Returning" value={stats.returning || 0} />
        <StatCard icon={Users} label="New members" value={stats.new_members || 0} />
        <StatCard icon={DollarSign} label="Deposit volume" value={formatMoney(stats.deposit_volume || 0)} tone="gold" />
        <StatCard icon={ReceiptText} label="Deposit count" value={stats.deposit_count || 0} />
      </div>

      <div className="rounded-xl border border-white/10 bg-[#121214]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-semibold tracking-wide">{tab === "new" ? "New Depositors" : "All Depositors"}</h2>
            <p className="mt-1 text-xs text-slate-500">Approved wallet deposits only. Game loads are not counted here.</p>
          </div>
          <div className="text-sm text-slate-400">Total members: {Number(stats.total_members || 0).toLocaleString()}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3 sm:px-6">Member</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Deposits</th>
                <th className="px-4 py-3 text-right">Count</th>
                <th className="px-4 py-3">First deposit</th>
                <th className="px-4 py-3">Last deposit</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user_id || row.email || row.username} className="border-t border-white/5">
                  <td className="px-4 py-4 sm:px-6">
                    <div className="font-semibold text-white"><VerifiedName name={row.username || "Member"} user={row} /></div>
                    <div className="mt-1 text-xs text-slate-400">{row.email || "-"}</div>
                    {row.is_new_member && <div className="mt-2 text-xs font-semibold text-emerald-300">New member this range</div>}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      row.is_new_depositor
                        ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                        : "border-white/15 bg-white/5 text-slate-200"
                    }`}>
                      {row.is_new_depositor ? "New depositor" : "Returning"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right font-mono text-base text-amber-300">{formatMoney(row.deposits)}</td>
                  <td className="px-4 py-4 text-right font-semibold">{Number(row.count || 0).toLocaleString()}</td>
                  <td className="px-4 py-4 text-slate-300">{formatDate(row.first_deposit)}</td>
                  <td className="px-4 py-4 text-slate-300">{formatDate(row.last_deposit)}</td>
                  <td className="px-4 py-4 text-right">
                    <a
                      href={`/admin/transactions`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-white hover:border-amber-400/60"
                    >
                      Open <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    No approved wallet depositors found for this range.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    Loading depositors...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-semibold ${
        active
          ? "border-amber-400 bg-amber-400 text-black"
          : "border-white/10 bg-[#101012] text-white hover:border-amber-400/50"
      }`}
    >
      {children}
    </button>
  );
}

function DateInput({ value, onChange }) {
  return (
    <label className="relative">
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full min-w-[190px] rounded-full border border-white/10 bg-[#101012] px-4 pr-10 text-sm text-white outline-none focus:border-amber-400/60"
      />
      <CalendarDays className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </label>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-amber-500/20 bg-[#121214] p-4">
      <Icon className={`mb-4 h-5 w-5 ${tone === "gold" ? "text-amber-300" : "text-slate-400"}`} />
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-2 text-3xl font-black tabular-nums ${tone === "gold" ? "text-amber-300" : "text-white"}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function formatMoney(amount = 0) {
  return `$${((Number(amount) || 0) / 1000).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
